import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { ImageTileFlipType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { EVENT_EXPAND_STROKE_WIDTH } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/constants';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { GraphicsType, IEffect, IFillStyle, IFrame, IGraphicsOptions, StrokeType, IStrokeStyle, IImageFill, IGradFill, ICenterFrame } from '@tencent/alloy-graphics';
import { TilingMirror, LineCap } from '@tencent/alloy-renderer';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { isCopyToImageOverSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/copy-to-image/over-size-control';
import { DisplayRule, IFilterDisplayOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/filter/filter-display-scene';
import imageCache from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache';
import { notRotateWithShape } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/utils';
import { RenderPostionType } from '@src/js/constant';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { getLineJoin, StrokeLineCap } from '@editor/src/js/constants';
import { IRotateAndFlip } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { isSketchStyle } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/sketch-style';

const MirrorTypeMap = {
    [ImageTileFlipType.NONE]: TilingMirror.None,
    [ImageTileFlipType.x]: TilingMirror.Horizontal,
    [ImageTileFlipType.y]: TilingMirror.Vertical,
    [ImageTileFlipType.xy]: TilingMirror.Both,
};

export const MIN_STROKE_WIDTH = 1.5;
export const MIN_END_STROKE_WIDTH = 4;

interface IOptions {
    style: any;
    factoryOptions?: InterfaceFactoryOptions,
    frame?: IFrame;
    isOutsizeBorder?: boolean;
    realRotateAndFlip?: IRotateAndFlip,
    applyImageFilter?: boolean;
}

export function generateGraphicsModel(options: IOptions): IGraphicsOptions {
    const { style, frame = { left: 0, top: 0 }, factoryOptions, applyImageFilter = true, realRotateAndFlip } = options;

    // 边框为无边框并且设置了草绘类型时，使用边框 sketchStyle 中保存的 path 信息
    const pathStyle = hasNoLineStyle(style) && isSketchStyle(style.border?.sketchStyle?.type)
        ? style.border.sketchStyle : style;

    return {
        frame: {
            width: style.width,
            height: style.height,
            flipH: !!(realRotateAndFlip?.flipH),
            flipV: !!(realRotateAndFlip?.flipV),
            rotate: realRotateAndFlip?.rotate,
            ...frame,
            offsetFrame: options.style.offsetFrame,
        },
        path: {
            type: (pathStyle.bgShape || style.bgLine) ?? GraphicsType.RECT,
            controlValues: pathStyle.controlValues,
            pathList: pathStyle.pathList,
        },
        fillStyle: getFillStyle(style, factoryOptions, !!options.style.offsetFrame),
        strokeStyle: getStrokeStyle(options),
        filter: {
            effect: getEffectFilter(style.effect),
            imageFilter: applyImageFilter ? style.image : undefined,
            forbidWebgl: FORBID_WEBGL_FILTER,
            filterDisplayOptions: getFilterDisplayOptions(options),
        },
    };
}

export function getStrokeStyle(options: IOptions): IStrokeStyle | undefined {
    const { style, isOutsizeBorder, factoryOptions } = options;

    if (hasNoLineStyle(style)) {
        return;
    }

    const headEnd = style.border?.headEnd ? {
        ...style.border?.headEnd,
    } : undefined;
    const tailEnd = style.border?.tailEnd ? {
        ...style.border?.tailEnd,
    } : undefined;

    const { model } = factoryOptions || {};
    const isTextOrPlaceholderImage = (model as IShapeModel)?.subType === 1
        || (model as IShapeModel)?.isPlaceholder || (model as IPictureModel)?.isImage;
    const lineCap = StrokeLineCap.find(item => item.type === style.border?.linecap)?.val as LineCap;
    return {
        lineCap,
        lineJoin: getLineJoin(style.border?.linejoin, isTextOrPlaceholderImage).val,
        lineWidth: style.border?.width,
        fillStyle: getBorderFillStyle(style),
        headEnd,
        tailEnd,
        type: style.border?.style || StrokeType.SOLID,
        hitStrokeWidth: getHitStrokeWidth(style, factoryOptions),
        isOutsizeBorder,
        minStrokeWidth: MIN_STROKE_WIDTH,
        minEndStrokeWidth: MIN_END_STROKE_WIDTH,
        compoundType: style.border?.compoundType,
    };
}

export function isSpecialPath(type?: string): boolean {
    return type === GraphicsType.CLOUD_CALLOUT
        || type === GraphicsType.WEDGE_RECT_CALLOUT
        || type === GraphicsType.WEDGE_ROUND_RECT_CALLOUT
        || type === GraphicsType.WEDGE_ELLIPSE_CALLOUT;
}

function hasNoLineStyle(style): boolean {
    return !style?.border || (!style.border?.color && !style.border?.gradFill);
}

function getBorderFillStyle(style) {
    if (style.border?.gradFill) {
        const { gsList, lin, pathGradient, rotateWithShape } = style.border.gradFill;
        return {
            gradFill: {
                gsList: gsList.map(item => ({ pos: item.pos, color: item.color?.srgb_color })),
                lin,
                pathGradient,
                rotateWithShape: !notRotateWithShape(rotateWithShape),
            },
        };
    }
    return {
        solidFill: {
            color: style.border?.color,
        },
    };
}

function getFilterDisplayOptions(options: IOptions): IFilterDisplayOptions | undefined {
    const { style, factoryOptions } = options;

    const forbidFilter = isCopyToImageOverSize(
        factoryOptions?.exportImageManager?.isImageCopying,
        style.width,
        style.height,
    );

    if (forbidFilter) {
        return {
            shadow: DisplayRule.None,
            glow: DisplayRule.None,
            softEdge: DisplayRule.None,
            reflection: DisplayRule.None,
            image: DisplayRule.None,
        };
    }

    return factoryOptions?.getFilterDisplayOptions?.();
}

function getPageScale(factoryOptions?: InterfaceFactoryOptions): number | undefined {
    if (!factoryOptions) {
        return;
    }
    const { region, getPageScale } = factoryOptions;
    if (region === RenderPostionType.LEFTLIST) {
        const scale = getPageScale?.();
        if (scale) {
            return ptToCanvas(scale);
        }
    }
}

export function getEffectFilter(oldEffect): IEffect | undefined {
    if (!oldEffect) {
        return;
    }
    const { shadow, glow, reflection, softEdge } = oldEffect;
    const effect: IEffect = {};
    if (shadow) {
        effect.shadow = {
            algn: shadow.algn,
            blur: shadow.blurRad,
            angle: shadow.dir,
            distance: shadow.dist,
            size: shadow.size,
            scaleX: shadow.sizeX,
            scaleY: shadow.sizeY,
            isOuter: shadow.isOuter,
            color: shadow.color,
            skewX: shadow.skewX,
            skewY: shadow.skewY,
            opacity: shadow.opacity,
        };
    }
    if (glow) {
        effect.glow = {
            ...glow,
        };
    }
    if (reflection) {
        effect.reflection = {
            blur: reflection.blurRad,
            angle: reflection.dir,
            distance: reflection.dist,
            size: reflection.size,
            endOpacity: reflection.endOpacity,
            startOpacity: reflection.startOpacity,
            rotateWithShape: !notRotateWithShape(reflection.rotWithShape),
        };
    }
    if (softEdge) {
        effect.softEdge = {
            ...softEdge,
        };
    }

    return effect;
}

export function getFillStyle(style, factoryOptions?, isGroupFill?: boolean): IFillStyle | undefined {
    if (!style.fillBackground) {
        return;
    }
    const { noFill, solidFill, image, pattFill, gradFill } = style.fillBackground;

    if (noFill) {
        return {
            noFill: true,
        };
    }
    if (solidFill) {
        return {
            solidFill: {
                color: solidFill.color?.srgb_color,
            },
        };
    }
    if (image) {
        return getImageFill(image, style.isForeground, isGroupFill);
    }
    if (gradFill) {
        return getGradFill(gradFill, style.shadeTitleFrame, isGroupFill);
    }
    if (pattFill) {
        const { preset, bgColor, fgColor } = pattFill;
        return {
            patternFill: {
                preset,
                bgColor: bgColor.color?.srgb_color,
                fgColor: fgColor.color?.srgb_color,
                pageScale: getPageScale(factoryOptions),
            },
        };
    }
}

function getHitStrokeWidth(style, factoryOptions?: InterfaceFactoryOptions) {
    const { border } = style;
    if (!border) {
        return 0;
    }
    if (needExpandEventArea(style)) {
        const scale = factoryOptions?.getPageScale?.() || 1;
        return (border.width || 0) + (EVENT_EXPAND_STROKE_WIDTH / scale);
    }
    return border.width || 0;
}

function needExpandEventArea(style): boolean {
    const { fillBackground } = style;
    // 移动端有填充时，边框不需要扩展事件区域
    return !(browser.isMobile && fillBackground && !fillBackground?.noFill);
}

function getImageFill(image, isForeground?: boolean, isGroupFill?: boolean): {imageFill: IImageFill} {
    const { src, alpha, rotWithShape, tile, stretch } = image;
    return {
        imageFill: {
            src: imageCache.limitImageQuality(src),
            alpha,
            // 组合填充子元素默认不跟随形状旋转
            rotateWithShape: isGroupFill ? false : (isForeground || !notRotateWithShape(rotWithShape)),
            tile: tile ? {
                tx: tile.tx,
                ty: tile.ty,
                sx: tile.sx,
                sy: tile.sy,
                algn: tile.algn,
                flip: MirrorTypeMap[tile.flip],
            } : undefined,
            stretch,
        },
    };
}

function getGradFill(gradFill, centerFrame?: ICenterFrame, isGroupFill?: boolean): {gradFill: IGradFill} {
    const { gsList, lin, pathGradient, rotateWithShape, tileRect } = gradFill;
    return {
        gradFill: {
            gsList: gsList.map(item => ({ pos: item.pos, color: item.color?.srgb_color || item.color?.srgbColor })),
            lin,
            pathGradient,
            // 组合填充子元素默认不跟随形状旋转
            rotateWithShape: isGroupFill ? false : !notRotateWithShape(rotateWithShape),
            tileRect,
            centerFrame,
        },
    };
}
